import React from 'react';
import PropTypes from 'prop-types';
import {connect} from "react-redux";
import {addDays, parseISO} from "date-fns";
import {Button, Checkbox, Icon, Input, InputHint} from "spectre-react/dist/cjs";
import DateInput from "../../shared/daypicker/DateInput";
import TimeInput from "../../shared/other/TimeInput";
import ArrayField from "../../shared/other/ArrayField";
import ObjectField from "../../shared/other/ObjectField";
import {isoFmt, parseDate} from "../../shared/utils";

const THRESHOLD_TIME = '08:00';


class WorkHoursTable extends React.PureComponent {
    static propTypes = {
        engineerIds: PropTypes.arrayOf(PropTypes.string),
        workHours: PropTypes.arrayOf(PropTypes.shape({
            startDate: PropTypes.string,
            startTime: PropTypes.string,
            endDate: PropTypes.string,
            endTime: PropTypes.string,
            breakMinutes: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        })),
        onChange: PropTypes.func,
        shouldAddItemOnMount: PropTypes.bool,
        allowRemoveAllRows: PropTypes.bool,
    };

    state = {
        dateAutoChangeEnabled: true,
    }

    onDateAutoChangeEnabledChange = (ev) => {
        this.setState({dateAutoChangeEnabled: ev.target.checked});
    }

    componentDidMount() {
        if (this.props.shouldAddItemOnMount) {
            const newItem = this.addWorkHoursItem();
            setTimeout(() => this.onChange({target: {name: 'workHours', value: [newItem]}}), 10);
        }
    }

    onChange = (ev) => {
        let items = ev.target.value;

        // keep at least one item
        if (ev.type === ArrayField.EVENT_TYPES.REMOVE && items.length === 0)
            if (!this.props.allowRemoveAllRows)
                return;

        this.props.onChange(ev);
    };

    addWorkHoursItem = () => {
        let workHours = this.props.workHours;
        let date = isoFmt(new Date());

        if (workHours.length > 0) {
            const lastItem = workHours[workHours.length - 1];

            if (lastItem.endDate && lastItem.endTime) {
                if (lastItem.endTime.localeCompare(THRESHOLD_TIME) < 0)
                    date = lastItem.endDate;
                else
                    date = isoFmt(addDays(parseISO(lastItem.endDate), 1));
            }
        }

        const engineerIds = this.props.engineerIds;
        const engineerPresence = Object.fromEntries(engineerIds.map(engineerId => [engineerId, true]));
        return {startDate: date, startTime: '10:00', endDate: date, endTime: '19:00', breakMinutes: '60', engineerPresence};
    }

    render() {
        const {engineerIds, engineersById} = this.props;
        const {dateAutoChangeEnabled} = this.state;

        return (
            <div>
                <div className="mb-3">
                    <Checkbox switch name="dateAutoChangeEnabled" checked={dateAutoChangeEnabled} onChange={this.onDateAutoChangeEnabledChange}>
                        Включить автозамену конечной даты
                    </Checkbox>
                    <InputHint renderAs="div">
                        Если присутствуют проблемы с заполнением формы, можно попробовать отключить
                    </InputHint>
                </div>
                <ArrayField key={dateAutoChangeEnabled} name="workHours" array={this.props.workHours} onChange={this.onChange} buildNewItem={this.addWorkHoursItem}>
                    {({array, keys, onAdd, onRemove, onChange, onInsert}) =>
                        <fieldset className="mb-6" id="work-hours-table">
                            <legend className="h2">
                                Проведение работ&nbsp;

                                <Button action link small className="ml-2" onClick={onAdd}>
                                    <Icon icon="plus"/>
                                </Button>
                            </legend>

                            <div>
                                <div className="d-flex mb-3">
                                    <div className="flex-1 text-bold text-small text-uppercase">Начало</div>
                                    <div className="flex-1 ml-2"></div>
                                    <div className="ml-2"><span className="d-invisible">—</span></div>
                                    <div className="flex-1 ml-2 text-bold text-small text-uppercase">Окончание</div>
                                    <div className="flex-1 ml-2"></div>
                                    <div className="ml-2"><span className="d-invisible">—</span></div>
                                    <div className="flex-1 ml-2 text-bold text-small text-ellipsis col-delay" title="Перерыв">
                                        <span className="text-uppercase">Перерыв</span> (минуты)
                                    </div>
                                    {engineerIds.length > 1 && <div className="ml-2"><span className="d-invisible">—</span></div>}
                                    {engineerIds.length > 1 && <div className="ml-2 d-invisible">
                                        {engineerIds.map(engineerId => (
                                            <Checkbox inline className="my-0" key={engineerId}>
                                                {engineersById[engineerId].nameLfp0}
                                            </Checkbox>
                                        ))}
                                    </div>}
                                    <div className="ml-2"><span className="d-invisible">—</span></div>
                                    <div className="ml-2">
                                        <Button action small className="d-invisible">
                                            <Icon icon="minus"/>
                                        </Button>
                                    </div>
                                </div>

                                {array.map((item, index) => {
                                    const showInsertButton = index < array.length - 1;
                                    const key = keys[index] + Number(showInsertButton);

                                    return <WorkHoursItem index={index} key={key} engineerIds={engineerIds}
                                                          item={item} onChange={onChange} onRemove={onRemove}
                                                          showInsertButton={showInsertButton} onInsert={onInsert}
                                                          engineersById={engineersById}
                                                          dateAutoChangeEnabled={dateAutoChangeEnabled}
                                    />;
                                })}
                            </div>

                            <Button primary className="mt-2" onClick={onAdd}>Добавить</Button>
                        </fieldset>
                    }
                </ArrayField>
            </div>
        )
    }
}

class WorkHoursItem extends React.PureComponent {
    static propTypes = {
        engineersById: PropTypes.object,
        engineerIds: PropTypes.arrayOf(PropTypes.string),
        index: PropTypes.number,
        item: PropTypes.shape({
            startDate: PropTypes.string,
            startTime: PropTypes.string,
            endDate: PropTypes.string,
            endTime: PropTypes.string,
            breakMinutes: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        }),
        onChange: PropTypes.func,
        onRemove: PropTypes.func,
        onInsert: PropTypes.func,
        showInsertButton: PropTypes.bool,
        dummy: PropTypes.number,
        dateAutoChangeEnabled: PropTypes.bool,
    }

    onChange = (ev) => {
        const value = {...ev.target.value};

        if (this.props.dateAutoChangeEnabled) {
            const {startTime, endTime} = ev.target.value;

            const startTimeValid = startTime && TimeInput.isValidValue(startTime);
            const endTimeValid = endTime && TimeInput.isValidValue(endTime);

            if (startTimeValid && endTimeValid && endTime.localeCompare(startTime) < 0) {
                value.endDate = isoFmt(addDays(parseDate(value.startDate), 1));
            } else {
                value.endDate = value.startDate;
            }
        }

        ev = {target: {name: ev.target.name, value: value}};
        this.props.onChange(ev);
    }

    onEngineerPresenceChange = (ev) => {
        const {name, checked} = ev.target;
        const [,indexStr,,engineerId] = name.split('.');
        const index = parseInt(indexStr, 10);

        const item = this.props.item;
        const itemWithChanges = {...item, engineerPresence: {...item.engineerPresence}};
        itemWithChanges.engineerPresence[engineerId] = checked;

        this.onChange({target: {name: `workHours.${index}`, value: itemWithChanges}});
    }

    render() {
        const {engineersById, engineerIds, index, item, onRemove, showInsertButton, onInsert} = this.props;

        return (
            <ObjectField object={item} onChange={this.onChange} dummy={index}>
                {({onChange, object}) =>
                    <React.Fragment>
                        <div className="d-flex work-hours-table-row">
                            <div className="flex-1">
                                <DateInput name={`workHours.${index}.startDate`} className="form-input input-sm" placeholder="Пример: 26.03.2015" value={object.startDate} onChange={onChange}/>
                            </div>
                            <div className="flex-1 ml-2">
                                <TimeInput name={`workHours.${index}.startTime`} className="form-input input-sm" placeholder="Пример: 10:00" value={object.startTime} onChange={onChange}/>
                            </div>
                            <div className="ml-2">—</div>
                            <div className="flex-1 ml-2">
                                <DateInput readOnly name={`workHours.${index}.endDate`} className="form-input input-sm" value={object.endDate} onChange={onChange}/>
                            </div>
                            <div className="flex-1 ml-2">
                                <TimeInput name={`workHours.${index}.endTime`} className="form-input input-sm" placeholder="Пример: 18:00" value={object.endTime} onChange={onChange}/>
                            </div>
                            <div className="ml-2">—</div>
                            <div className="flex-1 ml-2">
                                <Input small type="number" min="0" step="1" name={`workHours.${index}.breakMinutes`}
                                       placeholder="Пример: 60" value={object.breakMinutes} onChange={onChange}/>
                            </div>
                            {engineerIds.length > 1 && <div className="ml-2">—</div>}
                            {engineerIds.length > 1 && <div className="ml-2">
                                {engineerIds.map(engineerId => (
                                    <Checkbox inline className="my-0" key={engineerId}
                                              name={`workHours.${index}.engineerPresence.${engineerId}`}
                                              checked={item.engineerPresence[engineerId]}
                                              onChange={this.onEngineerPresenceChange}>
                                        {engineersById[engineerId].nameLfp0}
                                    </Checkbox>
                                ))}
                            </div>}
                            <div className="ml-2">—</div>
                            <div className="ml-2">
                                <Button action small onClick={onRemove} data-index={index}>
                                    <Icon icon="minus"/>
                                </Button>
                            </div>
                        </div>
                        {showInsertButton && <div className="btn-add-item" data-index-before={index + 1} onClick={onInsert}></div>}
                    </React.Fragment>
                }
            </ObjectField>
        )
    }
}

WorkHoursTable = connect(({engineersById}) => ({engineersById}), null)(WorkHoursTable);


export default WorkHoursTable;